import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getWorkerApiUrl } from '../../../../api';
import { useTranslation } from 'react-i18next';
import {
  InformationCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  StarIcon
} from '@heroicons/react/24/outline';

// Storage keys for persisting data
const STORAGE_KEY_KEYWORDS = 'serpapi_suggested_keywords';
const STORAGE_KEY_SOURCES = 'serpapi_keyword_sources';
const DISPLAYED_SUGGESTIONS_COUNT = 10;
const SOURCE_PRIORITY = {
  Manual: 0,
  'Top Results': 0,
  'Google Autocomplete': 1,
  'Related Searches': 2,
  'Related Questions': 3,
  Unknown: 99
};

// Add this helper function for logging on localhost only
const isLocalhost = () => {
  return window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
};

// Custom logging function
const log = (...args) => {
  if (isLocalhost()) {
    console.log('[SerpApiKeywords]', ...args);
  }
};

// Load data from localStorage
const loadFromStorage = (key, defaultValue) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (e) {
    console.error(`Error loading from localStorage for key ${key}:`, e);
    return defaultValue;
  }
};

// Clear SerpApi cache (useful for debugging)
const clearSerpApiCache = () => {
  try {
    localStorage.removeItem(STORAGE_KEY_KEYWORDS);
    localStorage.removeItem(STORAGE_KEY_SOURCES);
    console.log('[SerpApiKeywords] Cache cleared');
  } catch (e) {
    console.error('[SerpApiKeywords] Error clearing cache:', e);
  }
};

// Save data to localStorage
const saveToStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error(`Error saving to localStorage for key ${key}:`, e);
  }
};

const normalizeKeywordValue = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const KEYWORD_STOPWORDS = new Set([
  // PL
  'czy', 'jak', 'co', 'kto', 'gdzie', 'kiedy', 'ktory', 'ktora', 'ktore', 'który', 'która', 'które',
  'jaki', 'jaka', 'jakie', 'ile', 'oraz', 'albo', 'lub', 'i', 'a', 'o', 'w', 'z', 'na', 'do', 'po',
  // EN
  'is', 'are', 'am', 'do', 'does', 'did', 'what', 'when', 'where', 'who', 'why', 'how',
  'the', 'a', 'an', 'and', 'or', 'to', 'of', 'in', 'on', 'for'
]);

const extractComparableTokens = (value = '', { minLength = 3, removeStopwords = true } = {}) => {
  const normalized = normalizeKeywordValue(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized) return [];

  return normalized
    .split(' ')
    .map((token) => token.trim())
    .filter((token) => token.length >= minLength)
    .filter((token) => !(removeStopwords && KEYWORD_STOPWORDS.has(token)));
};

const isTruncatedKeyword = (value) => /(?:\.\.\.|…)\s*$/.test(normalizeKeywordValue(value));

const isJunkKeyword = (value) => {
  const keyword = normalizeKeywordValue(value);
  if (!keyword) return true;
  if (isTruncatedKeyword(keyword)) return true;
  const comparableTokens = extractComparableTokens(keyword, { minLength: 2, removeStopwords: true });
  const rawTokens = extractComparableTokens(keyword, { minLength: 2, removeStopwords: false });
  if (rawTokens.length === 1 && KEYWORD_STOPWORDS.has(rawTokens[0])) return true;
  if (rawTokens.length > 0 && comparableTokens.length === 0) return true;

  const lower = keyword.toLowerCase();
  if (/^https?:\/\//i.test(lower)) return true;
  if (/www\./i.test(lower)) return true;
  if (/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv|json|xml)(\b|$)/i.test(lower)) return true;
  if (/^[-_/.:?=&%0-9]+$/.test(lower)) return true;
  if (keyword.length > 120) return true;

  const slashCount = (keyword.match(/[\\/]/g) || []).length;
  if (slashCount >= 2) return true;

  // Require at least one letter to avoid pure IDs/fragments.
  if (!/[a-ząćęłńóśźż]/i.test(keyword)) return true;

  return false;
};

const dedupeKeywords = (items = []) => {
  const normalizedMap = new Map();
  items.forEach((raw) => {
    const keyword = normalizeKeywordValue(raw);
    if (!keyword || isJunkKeyword(keyword)) return;
    const key = keyword.toLowerCase();
    if (!normalizedMap.has(key)) {
      normalizedMap.set(key, keyword);
    }
  });
  return Array.from(normalizedMap.values());
};

const isKeywordRelevantToIntent = (keyword = '', intentTokens = new Set()) => {
  if (!(intentTokens instanceof Set) || intentTokens.size === 0) return true;
  const keywordTokens = extractComparableTokens(keyword, { minLength: 3, removeStopwords: true });
  if (keywordTokens.length === 0) return false;
  return keywordTokens.some((token) => intentTokens.has(token));
};

const SerpApiKeywords = ({ 
  articleContext, 
  keywords, 
  onKeywordSelect, 
  onKeywordRemove = () => {},
  maxKeywords = 3,
  triggerSearch, 
  language = 'english',
  onSearchComplete = () => {},
  onClose = () => {}
}) => {
  const { t } = useTranslation();
  // Initialize state from localStorage if available
  const [suggestedKeywords, setSuggestedKeywords] = useState(() => 
    loadFromStorage(STORAGE_KEY_KEYWORDS, [])
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [keywordSources, setKeywordSources] = useState(() => 
    loadFromStorage(STORAGE_KEY_SOURCES, {})
  );
  
  // Use a counter to track search attempts
  const searchAttemptRef = React.useRef(0);
  
  // Use a ref to track the last trigger value we've responded to
  const lastTriggerRef = React.useRef(0);

  const [isEnglishFallbackEnabled] = useState(language !== 'english');
  const [showEnglishResults, setShowEnglishResults] = useState(false);
  const [englishKeywords, setEnglishKeywords] = useState([]);
  const [, setEnglishKeywordSources] = useState({});
  const mergedKeywords = useMemo(() => {
    return dedupeKeywords([...(keywords || []), ...(suggestedKeywords || [])]);
  }, [keywords, suggestedKeywords]);

  const sortedSuggestedKeywords = useMemo(() => {
    return [...mergedKeywords].sort((a, b) => {
      const sourceLabelA = keywordSources[a] || (keywords.includes(a) ? 'Manual' : 'Unknown');
      const sourceLabelB = keywordSources[b] || (keywords.includes(b) ? 'Manual' : 'Unknown');
      const sourceA = SOURCE_PRIORITY[sourceLabelA] ?? 99;
      const sourceB = SOURCE_PRIORITY[sourceLabelB] ?? 99;
      if (sourceA !== sourceB) return sourceA - sourceB;
      return a.localeCompare(b, language === 'polski' ? 'pl' : 'en');
    });
  }, [mergedKeywords, keywordSources, language, keywords]);

  const displayedSuggestedKeywords = useMemo(() => {
    return sortedSuggestedKeywords.slice(0, DISPLAYED_SUGGESTIONS_COUNT);
  }, [sortedSuggestedKeywords]);

  const estimateKeywordMetrics = useCallback((keyword) => {
    const source = keywordSources[keyword] || (keywords.includes(keyword) ? 'Manual' : 'Related Searches');
    const lower = keyword.toLowerCase();
    const wordsCount = keyword.trim().split(/\s+/).filter(Boolean).length;

    const sourceBase = {
      'Top Results': 8,
      'Google Autocomplete': 7,
      'Related Searches': 6,
      'Related Questions': 5
    }[source] || 5;

    const highIntentTokens = ['cena', 'koszt', 'kup', 'ranking', 'best', 'price', 'review', 'vs', 'comparison', 'alternatywa', 'top'];
    const mediumIntentTokens = ['jak', 'co', 'dlaczego', 'guide', 'tutorial', 'strategia', 'narzędzia'];
    const highHits = highIntentTokens.filter((token) => lower.includes(token)).length;
    const mediumHits = mediumIntentTokens.filter((token) => lower.includes(token)).length;

    const valueLevel = Math.min(3, Math.max(1, (highHits > 0 ? 2 : 1) + (highHits > 1 ? 1 : 0)));
    const competitionLevel = Math.min(3, Math.max(1, Math.round((sourceBase + wordsCount) / 5)));
    const volumeScore = Math.min(10, Math.max(1, sourceBase + Math.min(2, mediumHits) - (wordsCount >= 7 ? 1 : 0)));

    const trendDirection = highHits > 0 ? 'up' : mediumHits > 0 ? 'stable' : 'down';
    const trendDelta = trendDirection === 'up' ? '+12%' : trendDirection === 'down' ? '-4%' : '0%';

    const recommendationScore = (valueLevel * 10) + volumeScore - (competitionLevel * 2);

    return {
      source,
      valueLevel,
      competitionLevel,
      volumeScore,
      trendDirection,
      trendDelta,
      recommendationScore
    };
  }, [keywordSources, keywords]);

  const rankedKeywords = useMemo(() => {
    return displayedSuggestedKeywords
      .map((keyword) => ({
        keyword,
        metrics: estimateKeywordMetrics(keyword)
      }))
      .sort((a, b) => b.metrics.recommendationScore - a.metrics.recommendationScore);
  }, [displayedSuggestedKeywords, estimateKeywordMetrics]);

  const recommendedKeywordSet = useMemo(() => {
    return new Set(rankedKeywords.slice(0, maxKeywords).map((item) => item.keyword));
  }, [rankedKeywords, maxKeywords]);
  
  // Log component props when they change
  useEffect(() => {
    log('Component mounted/updated with props:', { 
      articleContext: articleContext ? articleContext.substring(0, 50) + '...' : null, 
      keywordsCount: keywords.length,
      triggerSearch,
      lastTrigger: lastTriggerRef.current,
      suggestedKeywordsCount: suggestedKeywords.length
    });
    
    // Clear cache if article context has changed significantly
    if (articleContext && articleContext.length > 10) {
      const lastContext = localStorage.getItem('lastArticleContext');
      if (lastContext && lastContext !== articleContext) {
        log('Article context changed, clearing SerpApi cache');
        clearSerpApiCache();
      }
      localStorage.setItem('lastArticleContext', articleContext);
    }
  }, [articleContext, keywords, triggerSearch, suggestedKeywords.length]);

  // Save suggestedKeywords to localStorage when they change
  useEffect(() => {
    if (suggestedKeywords.length > 0) {
      log('Saving suggestedKeywords to localStorage:', suggestedKeywords.length);
      saveToStorage(STORAGE_KEY_KEYWORDS, suggestedKeywords);
    }
  }, [suggestedKeywords]);

  // Save keywordSources to localStorage when they change
  useEffect(() => {
    if (Object.keys(keywordSources).length > 0) {
      log('Saving keywordSources to localStorage:', Object.keys(keywordSources).length);
      saveToStorage(STORAGE_KEY_SOURCES, keywordSources);
    }
  }, [keywordSources]);

  // Helper function to extract a more search-friendly query from context
  const getSearchQuery = (context) => {
    const raw = normalizeKeywordValue(context);
    if (!raw) return '';

    const withoutTags = raw.replace(/<[^>]*>/g, ' ');
    const withoutUrls = withoutTags.replace(/https?:\/\/\S+/gi, ' ');
    const withoutFiles = withoutUrls.replace(/\b[\w.-]+\.(pdf|docx?|xlsx?|pptx?|txt|csv|json|xml)\b/gi, ' ');
    const normalized = withoutFiles
      .replace(/docelowa\s+d[łl]ugo[śćsc]\s*:\s*[\d\s\-–—a-ząćęłńóśźż]+/gi, ' ')
      .replace(/[|()[\]{}]/g, ' ')
      .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!normalized) return '';
    const words = normalized.split(' ').filter(Boolean).slice(0, 8);
    return words.join(' ');
  };

  // Helper function to convert language to SerpAPI format
  const getLanguageParams = (lang) => {
    // Map app language to SerpAPI parameters
    const langMap = {
      'polish': { hl: 'pl', gl: 'pl', lr: 'lang_pl' },
      'english': { hl: 'en', gl: 'us', lr: 'lang_en' },
      'german': { hl: 'de', gl: 'de', lr: 'lang_de' },
      // Add more languages as needed
    };
    
    return langMap[lang.toLowerCase()] || { hl: 'en', gl: 'us', lr: 'lang_en' };
  };

  const fetchSerpApiKeywords = async (query, useLang = language, isFallback = false) => {
    const attemptNumber = ++searchAttemptRef.current;
    const searchQuery = getSearchQuery(query);
    log(`KEYWORD FLOW: Starting API call attempt #${attemptNumber} with query:`, searchQuery);
    log(`KEYWORD FLOW: Using language: ${useLang}${isFallback ? ' (fallback)' : ''}`);
    
    if (!isFallback) {
      setIsLoading(true);
    }
    setError(null);
    
    try {
      const langParams = getLanguageParams(useLang);
      log('KEYWORD FLOW: Language parameters:', langParams);

      const token = localStorage.getItem('directus_token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const apiUrl = getWorkerApiUrl('/api/keywords');
      log(`KEYWORD FLOW: API URL: ${apiUrl}`);

      const requestConfig = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 35000
      };

      let response;
      let lastError = null;
      for (let attempt = 1; attempt <= 2; attempt += 1) {
        try {
          response = await axios.post(apiUrl, {
            query: searchQuery,
            language: useLang
          }, requestConfig);
          lastError = null;
          break;
        } catch (postError) {
          lastError = postError;
          const status = postError?.response?.status;
          const timedOut = postError?.code === 'ECONNABORTED' || /timeout/i.test(postError?.message || '');
          const shouldRetryAsGet = status === 405 || status === 404;

          if (shouldRetryAsGet) {
            log(`KEYWORD FLOW: Attempt #${attemptNumber}: POST failed with ${status}, retrying as GET`);
            response = await axios.get(apiUrl, {
              params: {
                query: searchQuery,
                language: useLang
              },
              headers: {
                'Authorization': `Bearer ${token}`
              },
              timeout: 35000
            });
            lastError = null;
            break;
          }

          if (timedOut && attempt === 1) {
            log(`KEYWORD FLOW: Attempt #${attemptNumber}: timeout, retrying once`);
            await new Promise((resolve) => setTimeout(resolve, 400));
            continue;
          }

          throw postError;
        }
      }

      if (!response?.data) {
        throw lastError || new Error('Keyword API returned an empty response');
      }

      if (response.data.success === false) {
        throw new Error(response.data.error || response.data.message || 'Keyword API returned success=false');
      }
      
      log(`KEYWORD FLOW: Attempt #${attemptNumber}: Success with backend API`);
      
      const data = response?.data?.data ?? response?.data;
      
      log(`KEYWORD FLOW: Attempt #${attemptNumber}: Data parsed successfully`);
      
      const processedResults = processResults(data);
      log(`KEYWORD FLOW: Attempt #${attemptNumber}: Results processed:`, { 
        keywordCount: processedResults.keywords.length,
        sourceTypes: Object.keys(processedResults.sources).length
      });
      
      log('KEYWORD FLOW: Final API params:', {
        query: searchQuery,
        engine: 'google',
        language: langParams,
        num: 10
      });
      
      return {
        success: true,
        ...processedResults
      };
    } catch (error) {
      log(`KEYWORD FLOW: Attempt #${attemptNumber}: Error in API call:`, error.message);
      console.error('Error fetching keywords from SerpAPI:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch keywords'
      };
    } finally {
      if (!isFallback) {
        setIsLoading(false);
      }
    }
  };
  
  const processResults = (data) => {
    try {
      log('Processing results from API');
      const sources = {};
      let allKeywords = [];
      const intentTokens = new Set([
        ...extractComparableTokens(articleContext, { minLength: 3, removeStopwords: true }),
        ...extractComparableTokens((keywords || []).join(' '), { minLength: 3, removeStopwords: true })
      ]);
      
      // Log the raw data structure to help debug
      log('API response data structure:', Object.keys(data).join(', '));
      
      // Process related searches
      if (data.related_searches && Array.isArray(data.related_searches)) {
        const relatedSearches = dedupeKeywords(data.related_searches.map(item => item.query).filter(Boolean));
        log('Found related searches:', relatedSearches.length);
        relatedSearches.forEach(keyword => {
          sources[keyword] = 'Related Searches';
        });
        allKeywords = [...allKeywords, ...relatedSearches];
      } else {
        log('No related searches found in API response');
      }
      
      // Process related questions
      if (data.related_questions && Array.isArray(data.related_questions)) {
        const relatedQuestions = dedupeKeywords(data.related_questions.map(item => item.question).filter(Boolean));
        log('Found related questions:', relatedQuestions.length);
        relatedQuestions.forEach(keyword => {
          sources[keyword] = 'Related Questions';
        });
        allKeywords = [...allKeywords, ...relatedQuestions];
      } else {
        log('No related questions found in API response');
      }
      
      // Process organic results
      if (data.organic_results && Array.isArray(data.organic_results)) {
        const organicTitlesRaw = data.organic_results
          .map(item => item.title)
          .filter(Boolean)
          .map(title => title.replace(/\s-\s.*$/, '').trim()); // Clean up titles
        const organicTitles = dedupeKeywords(organicTitlesRaw);
        
        log('Found organic titles:', organicTitles.length);
        organicTitles.forEach(keyword => {
          sources[keyword] = 'Top Results';
        });
        allKeywords = [...allKeywords, ...organicTitles];
      } else {
        log('No organic results found in API response');
      }
      
      const dedupedKeywords = dedupeKeywords(allKeywords);
      const filteredByIntent = dedupedKeywords.filter((item) => isKeywordRelevantToIntent(item, intentTokens));
      const finalKeywords = filteredByIntent.length >= 3 ? filteredByIntent : dedupedKeywords;

      log('Total keywords processed:', allKeywords.length);
      log('Intent filtering summary:', {
        before: dedupedKeywords.length,
        after: finalKeywords.length,
        intentTokens: Array.from(intentTokens).slice(0, 12)
      });

      return {
        keywords: finalKeywords,
        sources: sources
      };
    } catch (error) {
      log('Error processing results:', error.message);
      console.error('Error processing results:', error);
      return {
        keywords: [],
        sources: {}
      };
    }
  };

  // Autocomplete idzie przez uwierzytelniony endpoint article-queue
  // (klucz SerpAPI zostaje na serwerze, nigdy w bundlu przegladarki).
  const fetchAutocompleteSuggestions = async (query) => {
    log(`KEYWORD FLOW: Fetching autocomplete suggestions for: ${query}`);

    const trimmedQuery = String(query || '').trim();
    const token = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');
    if (!trimmedQuery || !token) {
      return [];
    }

    try {
      const response = await axios.post(
        getWorkerApiUrl('/api/keywords/autocomplete'),
        { query: trimmedQuery.slice(0, 300), language },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );

      const suggestions = Array.isArray(response?.data?.suggestions) ? response.data.suggestions : [];
      log(`KEYWORD FLOW: Found ${suggestions.length} autocomplete suggestions`);
      return suggestions;
    } catch (autocompleteError) {
      log(`KEYWORD FLOW: Autocomplete failed: ${autocompleteError.message}`);
      return [];
    }
  };

  // Modyfikacja funkcji performSearch, aby wyodrębniała autocomplete z regular search
  const performSearch = useCallback(async () => {
    log('KEYWORD FLOW: performSearch called');
    if (!articleContext || articleContext.trim().length <= 3) {
      log('KEYWORD FLOW: Article context too short, skipping search');
      onSearchComplete(false);
      return;
    }
    
    setIsLoading(true);
    setError('');
    log('KEYWORD FLOW: Starting search with article context:', articleContext.substring(0, 50) + '...');

    try {
      // Najpierw spróbuj pobrać dane z regularnego wyszukiwania
      const result = await fetchSerpApiKeywords(articleContext);
      log('KEYWORD FLOW: Search completed, success:', result.success);

      if (result.success) {
        const { keywords: regularKeywords, sources: regularSources } = result;
        log('KEYWORD FLOW: Keywords found:', regularKeywords.length);
        
        let autocompleteSuggestions = [];
        try {
          autocompleteSuggestions = await fetchAutocompleteSuggestions(articleContext);
        } catch (autocompleteError) {
          log('KEYWORD FLOW: Autocomplete skipped:', autocompleteError?.message);
          autocompleteSuggestions = [];
        }
        
        // Utwórz źródła dla autocomplete
        const autocompleteKeywordSources = {};
        autocompleteSuggestions.forEach(keyword => {
          autocompleteKeywordSources[keyword] = 'Google Autocomplete';
        });
        
        // Usuń duplikaty między autocomplete i regular keywords
        const uniqueRegularKeywords = regularKeywords.filter(
          keyword => !autocompleteSuggestions.includes(keyword)
        );
        
        // Połącz wszystkie słowa kluczowe, dając pierwszeństwo autocomplete
        const combinedKeywords = [...autocompleteSuggestions, ...uniqueRegularKeywords];
        const combinedSources = { ...autocompleteKeywordSources, ...regularSources };
        
        // Aktualizuj stan
        setSuggestedKeywords(combinedKeywords);
        setKeywordSources(combinedSources);
        
        // Kontynuuj z fallbackiem angielskim, jeśli to konieczne
        if (isEnglishFallbackEnabled && language !== 'english' && combinedKeywords.length < 5) {
          log('KEYWORD FLOW: Few results in primary language, trying English fallback');
          const englishResult = await fetchSerpApiKeywords(articleContext, 'english', true);
          
          if (englishResult.success && englishResult.keywords.length > 0) {
            log('KEYWORD FLOW: English fallback found', englishResult.keywords.length, 'keywords');
            setEnglishKeywords(englishResult.keywords);
            setEnglishKeywordSources(englishResult.sources);
            setShowEnglishResults(true);
          }
        }
        
        // Powiadom komponent nadrzędny, że wyszukiwanie jest zakończone
        onSearchComplete(true, combinedKeywords.length);
      } else {
        log('KEYWORD FLOW: Search failed:', result.error);
        setError(result.error || 'Failed to fetch keywords');
        onSearchComplete(false);
      }
    } catch (err) {
      log('KEYWORD FLOW: Unexpected error in performSearch:', err.message);
      console.error('Error in performSearch:', err);
      setError('An unexpected error occurred');
      onSearchComplete(false);
    } finally {
      setIsLoading(false);
      log('KEYWORD FLOW: Search process completed');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- internal fetch helpers are defined in component scope
  }, [articleContext, language, isEnglishFallbackEnabled, onSearchComplete]);

  // Effect to track changes to the triggerSearch prop
  useEffect(() => {
    log('triggerSearch changed:', triggerSearch, 'lastTrigger:', lastTriggerRef.current);
    
    // Only perform search if triggerSearch value has increased
    if (triggerSearch > lastTriggerRef.current) {
      log('KEYWORD FLOW: New search triggered with timestamp:', triggerSearch);
      log('KEYWORD FLOW: Article context length:', articleContext?.length);
      log('KEYWORD FLOW: Current keywords:', keywords);
      lastTriggerRef.current = triggerSearch;
      performSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to triggerSearch increments
  }, [triggerSearch, performSearch]);

  // Add useEffect to track and log changes to suggestedKeywords
  useEffect(() => {
    log('suggestedKeywords updated:', { count: suggestedKeywords.length });
  }, [suggestedKeywords]);
  
  // Toggle the English results visibility
  const toggleEnglishResults = () => {
    setShowEnglishResults(!showEnglishResults);
  };

  const handleSuggestedKeywordSelect = useCallback((keyword, isSelected) => {
    if (isSelected) {
      if (keywords.includes(keyword)) return;
      if (keywords.length >= maxKeywords) {
        return;
      }
      onKeywordSelect(keyword);
      return;
    }

    if (keywords.includes(keyword)) {
      onKeywordRemove(keyword);
    }
  }, [keywords, maxKeywords, onKeywordSelect, onKeywordRemove]);

  return (
    <div className="mt-6">
      
      {isLoading && (
        <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <span className="ml-2 text-gray-500">{t('articles.create.serpKeywords.loading')}</span>
        </div>
      )}
      
      {error && (
        <div className="p-4 bg-red-50 text-red-500 rounded-lg">
          <div className="font-medium">{t('articles.create.serpKeywords.errorTitle')}</div>
          <p className="text-sm mt-1">{error}</p>
          <p className="text-xs mt-2">{t('articles.create.serpKeywords.errorHint')}</p>
        </div>
      )}

      {!isLoading && !error && suggestedKeywords.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-base font-medium text-gray-800">
            {t('articles.create.keywordForm.selectionTitle', { selected: keywords.length, max: maxKeywords })}
          </h3>
          <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-[760px] w-full table-fixed divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-[40%] py-3 pl-4 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <span className="normal-case tracking-normal text-xs font-medium text-gray-700">
                        {t('articles.create.serpKeywords.sources.topResults')}
                      </span>
                    </th>
                    <th className="w-[18%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <span className="inline-flex items-center gap-1">
                        {t('articles.create.keywordForm.table.volume')}
                        <InformationCircleIcon
                          className="h-4 w-4 text-gray-400"
                          title={t('articles.create.keywordForm.table.volumeTooltip')}
                        />
                      </span>
                    </th>
                    <th className="w-[14%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t('articles.create.keywordForm.table.trend')}
                    </th>
                    <th className="w-[14%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t('articles.create.keywordForm.table.competition')}
                    </th>
                    <th className="w-[14%] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t('articles.create.keywordForm.table.value')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {rankedKeywords.map(({ keyword, metrics }) => (
                    <tr
                      key={keyword}
                      onClick={() => handleSuggestedKeywordSelect(keyword, !keywords.includes(keyword))}
                      className={`transition-colors ${
                        keywords.includes(keyword)
                          ? 'bg-gray-50 cursor-pointer'
                          : (keywords.length >= maxKeywords ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer')
                      }`}
                    >
                      <td className="px-4 py-3 align-middle">
                        <div className="flex min-w-0 items-center gap-3">
                          <input
                            type="checkbox"
                            checked={keywords.includes(keyword)}
                            disabled={keywords.length >= maxKeywords && !keywords.includes(keyword)}
                            onChange={(e) => handleSuggestedKeywordSelect(keyword, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="whitespace-normal break-words text-sm font-medium text-gray-900">{keyword}</span>
                              {recommendedKeywordSet.has(keyword) && (
                                <span className="inline-flex items-center rounded-full border border-gray-300 px-2 py-0.5 text-[11px] font-medium text-gray-700">
                                  {t('articles.create.keywordForm.recommended')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 align-middle">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-0.5">
                            {Array.from({ length: metrics.volumeScore }, (_, i) => (
                              <div key={`v-fill-${keyword}-${i}`} className={`h-3 w-1.5 rounded-sm ${metrics.volumeScore >= 8 ? 'bg-green-500' : metrics.volumeScore >= 5 ? 'bg-yellow-500' : 'bg-red-400'}`} />
                            ))}
                            {Array.from({ length: Math.max(0, 10 - metrics.volumeScore) }, (_, i) => (
                              <div key={`v-empty-${keyword}-${i}`} className="h-3 w-1.5 rounded-sm bg-gray-200" />
                            ))}
                          </div>
                          <span className="text-[11px] font-medium text-gray-500">{metrics.volumeScore}/10</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 align-middle">
                        <div className="flex items-center gap-1 text-gray-700">
                          {metrics.trendDirection === 'up' && <ArrowTrendingUpIcon className="h-4 w-4" />}
                          {metrics.trendDirection === 'down' && <ArrowTrendingDownIcon className="h-4 w-4" />}
                          {metrics.trendDirection === 'stable' && <MinusIcon className="h-4 w-4" />}
                          <span className="text-xs">{metrics.trendDelta}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 align-middle">
                        <div className="flex items-center gap-1">
                          {Array.from({ length: 3 }, (_, i) => (
                            <StarIcon
                              key={`c-${keyword}-${i}`}
                              className={`h-4 w-4 ${i < metrics.competitionLevel ? 'text-gray-700' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-3 align-middle">
                        <div className="flex items-center gap-1">
                          {Array.from({ length: 3 }, (_, i) => (
                            <span
                              key={`val-${keyword}-${i}`}
                              className={`inline-block h-2.5 w-2.5 rounded-full ${i < metrics.valueLevel ? 'bg-gray-700' : 'bg-gray-300'}`}
                            />
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* English fallback toggle - only show if language isn't English and we have English results */}
          {language !== 'english' && englishKeywords.length > 0 && (
            <div className="mt-4">
              <button
                onClick={toggleEnglishResults}
                className="text-sm text-primary-600 hover:text-primary-800 flex items-center"
              >
                {showEnglishResults
                ? t('articles.create.serpKeywords.englishToggleHide', { count: englishKeywords.length })
                : t('articles.create.serpKeywords.englishToggleShow', { count: englishKeywords.length })}
              </button>
            </div>
          )}
          
          {/* English fallback results */}
          {showEnglishResults && englishKeywords.length > 0 && (
            <div className="mt-2 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                <span>{t('articles.create.serpKeywords.englishKeywordsTitle')}</span>
                <span className="text-xs ml-2 text-gray-500">{t('articles.create.serpKeywords.englishKeywordsHint')}</span>
              </h4>
              <div className="flex flex-wrap gap-2">
                {englishKeywords.map((keyword, index) => (
                  <button
                    key={`english-${index}`}
                    onClick={() => handleSuggestedKeywordSelect(keyword, true)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors
                      ${keywords.includes(keyword)
                        ? 'bg-primary-100 text-primary-700'
                        : (keywords.length >= maxKeywords
                          ? 'bg-gray-100 border border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-white border border-gray-300 text-gray-700 hover:border-primary-500 hover:text-primary-700'
                        )}`}
                    disabled={keywords.length >= maxKeywords && !keywords.includes(keyword)}
                  >
                    {keyword}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!isLoading && !error && rankedKeywords.length === 0 && articleContext && (
        <div className="p-4 bg-gray-50 text-gray-500 rounded-lg">
          No keyword suggestions found. Try with a more specific article context.
          {isLocalhost() && (
            <div className="text-xs mt-2">
              Debug: searchAttempt: {searchAttemptRef.current}, 
              triggerSearch: {triggerSearch}, 
              articleContext length: {articleContext.length}
            </div>
          )}
        </div>
      )}


    </div>
  );
};

export default SerpApiKeywords; 